import { useEffect, useRef, useState } from 'react'
import { type Control, Controller, type FieldValues, type Path, type UseFormSetValue } from 'react-hook-form'

import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { StateSelect } from '@/components/ui/StateSelect'
import { useAddressAutocomplete } from '@/hooks/useAddressAutocomplete'
import { cn } from '@/lib/utils'

interface AddressFieldsProps<T extends FieldValues> {
  control: Control<T>
  setValue: UseFormSetValue<T>
  names: {
    line1: Path<T>
    /** Omit for models with no second address line. */
    line2?: Path<T>
    city: Path<T>
    state: Path<T>
    /** Omit for models with no zip, such as a claim's incident location. */
    zip?: Path<T>
  }
  required?: boolean
  /** Overrides the "Address" heading, e.g. to mark the whole block optional. */
  label?: string
  line1Placeholder?: string
  /** The state the address must be in: the select can't be changed by hand; a picked suggestion still fills it. */
  lockedState?: string
  disabled?: boolean
}

export function AddressFields<T extends FieldValues>({
  control,
  setValue,
  names,
  required,
  label = 'Address',
  line1Placeholder = 'Street address',
  lockedState,
  disabled,
}: AddressFieldsProps<T>) {
  const { predictions, search, select, clear } = useAddressAutocomplete()
  const [showDropdown, setShowDropdown] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  async function handleSelect(id: string) {
    const address = await select(id)
    // A suggestion outside the state the business is in would leave the disabled state field on it.
    if (address && (!lockedState || address.state === lockedState)) {
      setValue(names.line1, address.line1 as T[Path<T>], { shouldValidate: true, shouldDirty: true })
      setValue(names.city, address.city as T[Path<T>], { shouldValidate: true, shouldDirty: true })
      setValue(names.state, address.state as T[Path<T>], { shouldValidate: true, shouldDirty: true })
      if (names.zip)
        setValue(names.zip, address.zip as T[Path<T>], { shouldValidate: true, shouldDirty: true })
    }
    clear()
    setShowDropdown(false)
  }

  return (
    <Field>
      <Label>{label}</Label>
      <div className="flex flex-col gap-3">
        <div ref={containerRef} className="relative">
          <Controller
            name={names.line1}
            control={control}
            rules={{ required }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                placeholder={line1Placeholder}
                disabled={disabled}
                aria-invalid={fieldState.error ? true : undefined}
                autoComplete="off"
                onChange={(e) => {
                  field.onChange(e)
                  search(e.target.value)
                  setShowDropdown(true)
                }}
                onFocus={() => {
                  if (predictions.length > 0) setShowDropdown(true)
                }}
              />
            )}
          />
          {showDropdown && predictions.length > 0 && (
            <ul
              className={cn(
                'border-input bg-popover text-popover-foreground absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border shadow-md'
              )}
            >
              {predictions.map((prediction) => (
                <li
                  key={prediction.id}
                  className="hover:bg-accent hover:text-accent-foreground cursor-pointer px-3 py-2 text-sm"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleSelect(prediction.id)
                  }}
                >
                  {prediction.description}
                </li>
              ))}
            </ul>
          )}
        </div>
        {names.line2 && (
          <Controller
            name={names.line2}
            control={control}
            render={({ field }) => (
              <Input {...field} placeholder="Street address 2 (optional)" disabled={disabled} />
            )}
          />
        )}
        {/* Without a zip, State keeps StateSelect's 12rem cap from sm up and City fills the rest of the row. */}
        <div className={cn('grid gap-3', names.zip ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-[1fr_12rem]')}>
          <Controller
            name={names.city}
            control={control}
            rules={{ required }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                placeholder="City"
                disabled={disabled}
                aria-invalid={fieldState.error ? true : undefined}
              />
            )}
          />
          <Controller
            name={names.state}
            control={control}
            rules={{ required }}
            render={({ field, fieldState }) => (
              <StateSelect
                value={field.value}
                onChange={field.onChange}
                error={!!fieldState.error}
                clearable={!required && !lockedState}
                disabled={disabled || !!lockedState}
              />
            )}
          />
          {names.zip && (
            <Controller
              name={names.zip}
              control={control}
              rules={{ required }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  placeholder="Zip"
                  disabled={disabled}
                  aria-invalid={fieldState.error ? true : undefined}
                />
              )}
            />
          )}
        </div>
      </div>
    </Field>
  )
}
